import React, { useEffect, useRef, useState } from "react";
import AppShell from "@/components/layout/AppShell";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import api from "@/lib/api";
import { compressImageToDataURL } from "@/lib/image";
import { toast } from "sonner";
import {
  Plus, Edit, Trash2, Calendar, MapPin, Image as ImageIco, Upload, X,
  GalleryHorizontal, Check, BellRing, Users, UserCheck, UserX, DollarSign,
  ChevronDown, ImageOff, Pin, PinOff,
} from "lucide-react";

import { useCardExpansion } from "@/hooks/useCardExpansion";
import { CardThumb } from "@/components/cards/CardThumb";

const empty = {
  title: "", description: "", start_at: "", end_at: "", location: "",
  image_url: "", image_orientation: "portrait", price_cents: 0, published: true, show_on_landing: false,
  attending_reminder: "off", attending_reminder_message: "",
  not_responded_reminder: "off", not_responded_reminder_message: "",
};
const MAX_IMG_BYTES = 5 * 1024 * 1024; // 5MB

const REMINDER_OPTIONS = [
  { value: "off",    label: "Off — no reminder" },
  { value: "1d",     label: "Once, 1 day before" },
  { value: "3d",     label: "Once, 3 days before" },
  { value: "7d",     label: "Once, 7 days before" },
  { value: "daily7", label: "Daily for the last 7 days" },
  { value: "daily",  label: "Daily until the event" },
];

function toLocalInput(iso) {
  if (!iso) return "";
  try { const d = new Date(iso); const off = d.getTimezoneOffset(); return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16); } catch { return ""; }
}
function toISO(localStr) {
  if (!localStr) return "";
  try { return new Date(localStr).toISOString(); } catch { return localStr; }
}
function fmt(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return iso; }
}
function initials(first, last) {
  return `${(first || "")[0] || ""}${(last || "")[0] || ""}`.toUpperCase() || "?";
}

export default function AdminEvents() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [rsvpEvent, setRsvpEvent] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const { isExpanded, toggle: toggleImage } = useCardExpansion();
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const fileRef = useRef(null);

  const load = async () => { const { data } = await api.get("/events/admin/all"); setItems(data || []); };
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const startEdit = (ev) => {
    setEditing(ev);
    const { cta_label: _cl, cta_url: _cu, ...rest } = ev || {};
    setForm({
      ...empty,
      ...rest,
      start_at: toLocalInput(ev.start_at),
      end_at: toLocalInput(ev.end_at),
      price_cents: Number(ev.price_cents) || 0,
      attending_reminder: ev.attending_reminder || "off",
      attending_reminder_message: ev.attending_reminder_message || "",
      not_responded_reminder: ev.not_responded_reminder || "off",
      not_responded_reminder_message: ev.not_responded_reminder_message || "",
    });
    setOpen(true);
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (e.target) e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please pick an image file"); return; }
    if (f.type !== "image/png" && !f.name.toLowerCase().endsWith(".png")) { toast.error("Only PNG images are allowed."); return; }
    if (f.size > 15 * 1024 * 1024) { toast.error("Image must be 15MB or smaller before compression"); return; }

    // Portrait mode: validate exact 1080×1350 dimensions, no compression
    if (form.image_orientation === "portrait") {
      try {
        const img = await new Promise((resolve, reject) => {
          const i = new window.Image();
          i.onload = () => resolve(i);
          i.onerror = reject;
          i.src = URL.createObjectURL(f);
        });
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        URL.revokeObjectURL(img.src);
        if (w !== 1080 || h !== 1350) {
          toast.error(`Portrait images must be exactly 1080 × 1350 px. This image is ${w} × ${h} px.`);
          return;
        }
        // Pass through unchanged — no compression
        const reader = new FileReader();
        const dataUrl = await new Promise((resolve, reject) => {
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(f);
        });
        setForm((s) => ({ ...s, image_url: dataUrl }));
      } catch {
        toast.error("Could not read the file");
      }
      return;
    }

    try {
      // Landscape mode: validate exact 1920×1080 dimensions (16:9), no compression
      const img = await new Promise((resolve, reject) => {
        const i = new window.Image();
        i.onload = () => resolve(i);
        i.onerror = reject;
        i.src = URL.createObjectURL(f);
      });
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      URL.revokeObjectURL(img.src);
      if (w !== 1920 || h !== 1080) {
        toast.error(`Landscape images must be exactly 1920 × 1080 px (16:9). This image is ${w} × ${h} px.`);
        return;
      }
      const reader = new FileReader();
      const dataUrl = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(f);
      });
      setForm((s) => ({ ...s, image_url: dataUrl }));
    } catch {
      toast.error("Could not read the file");
    }
  };

  const clearImage = () => {
    setForm((s) => ({ ...s, image_url: "" }));
    if (fileRef.current) fileRef.current.value = "";
  };

  const pickFromGallery = (url) => {
    setForm((s) => ({ ...s, image_url: url }));
    setGalleryOpen(false);
  };

  const save = async () => {
    if (!form.title || !form.start_at) { toast.error("Title and start date/time are required"); return; }
    const payload = {
      ...form,
      cta_label: "",
      cta_url: "",
      start_at: toISO(form.start_at),
      end_at: form.end_at ? toISO(form.end_at) : null,
    };
    try {
      if (editing) await api.put(`/events/${editing.id}`, payload);
      else await api.post("/events", payload);
      toast.success("Event saved");
      setOpen(false); load();
    } catch { toast.error("Save failed"); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/events/${id}`);
      toast.success("Event deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Could not delete event");
    }
  };

  // ---- Pin dialog state ----
  // Opening the pin flow reveals a small confirmation dialog. Every pin
  // auto-expires at the end of the fixture's local day so admins don't
  // have to manually clean up. Unpin is a one-click action and skips the
  // dialog entirely.
  const [pinDialogEvent, setPinDialogEvent] = useState(null);
  const [pinSaving, setPinSaving] = useState(false);

  // Toggle the landing-page pin. Unpinning is one-click; pinning opens a
  // confirmation dialog that shows exactly when the pin will auto-drop.
  const togglePin = async (ev) => {
    if (ev.pinned_on_landing) {
      try {
        await api.post(`/events/${ev.id}/pin`, { pinned: false });
        toast.success("Unpinned");
        load();
      } catch (e) {
        toast.error(e?.response?.data?.detail || e?.message || "Could not update pin");
      }
      return;
    }
    setPinDialogEvent(ev);
  };

  const confirmPin = async () => {
    if (!pinDialogEvent) return;
    setPinSaving(true);
    try {
      // Auto-expire at the end of the event's local day (23:59) — gives
      // supporters the full day-of-fixture window before the pin drops.
      let expires_at = null;
      if (pinDialogEvent.start_at) {
        const s = new Date(pinDialogEvent.start_at);
        expires_at = new Date(s.getFullYear(), s.getMonth(), s.getDate(), 23, 59).toISOString();
      }
      await api.post(`/events/${pinDialogEvent.id}/pin`, {
        pinned: true,
        expires_at,
      });
      toast.success("Pinned to landing");
      setPinDialogEvent(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Could not pin event");
    } finally {
      setPinSaving(false);
    }
  };

  return (
    <AppShell variant="admin" title="Events" actions={
      <Button onClick={startNew} className="btn-accent-modern h-10" data-testid="events-new"><Plus className="h-4 w-4 mr-2" /> Add event</Button>
    }>
      {items.length === 0 ? (
        <Card className="p-14 text-center text-muted-foreground border-dashed rounded-2xl">
          <Calendar className="mx-auto h-8 w-8 mb-3 opacity-60" /> No events yet. Click <span className="font-medium text-foreground">Add event</span> to create one.
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((ev) => {
            const showImage = isExpanded(ev.id);
            return (
            <Card key={ev.id} className="overflow-hidden rounded-2xl hover-lift transition-all">
              {/* Full-width poster shown only when expanded. The default row
                  layout below already gives admins a small poster preview
                  on the left. */}
              {showImage && ev.image_url && (
                <div className="aspect-[21/9] max-h-[420px] bg-[hsl(var(--secondary))]">
                  <img src={ev.image_url} alt={ev.title} className="w-full h-full object-cover" />
                </div>
              )}
              <div className="flex items-stretch gap-4 p-4">
                {/* Compact thumbnail on the left — always visible so admins can
                    identify the poster at a glance even when the card is
                    collapsed. */}
                {!showImage && (<CardThumb
                  src={ev.image_url}
                  alt={ev.title}
                  className="h-24 w-32 sm:h-28 sm:w-40 rounded-xl"
                />)}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-[10px] font-mono-sm uppercase tracking-[0.18em] text-[hsl(var(--accent))]">{fmt(ev.start_at)}</div>
                    <div className="flex items-center gap-1.5">
                      {ev.pinned_on_landing && (
                        <Badge
                          variant="outline"
                          className="text-[10px] border-[hsl(var(--accent))]/40 text-[hsl(var(--accent))] gap-1"
                          data-testid={`event-pinned-badge-${ev.id}`}
                          title={ev.pin_expires_at ? `Auto-unpins ${fmt(ev.pin_expires_at)}` : "Pinned indefinitely"}
                        >
                          <Pin className="h-3 w-3" fill="currentColor" />
                          {ev.pin_expires_at ? `Pinned · until ${new Date(ev.pin_expires_at).toLocaleDateString(undefined, { day: "numeric", month: "short" })}` : "Pinned"}
                        </Badge>
                      )}
                      {!ev.published && <Badge variant="outline" className="text-[10px]">Draft</Badge>}
                    </div>
                  </div>
                  <div className="mt-1 text-lg font-semibold tracking-tight">{ev.title}</div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                    {ev.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {ev.location}</span>}
                    {(ev.price_cents || 0) > 0 && (
                      <span className="inline-flex items-center gap-1">
                        <DollarSign className="h-3 w-3" /> ${(ev.price_cents / 100).toFixed(2)}
                        {ev.attending_count ? (
                          <span
                            className="text-[10px]"
                            data-testid={`event-paid-summary-${ev.id}`}
                            title="Paid attendees / Total attending"
                          >
                            &middot; <span className="font-semibold text-foreground">{ev.paid_count || 0}</span>
                            {" / "}
                            {ev.attending_count} paid
                          </span>
                        ) : null}
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      <span className="font-semibold text-foreground">{ev.attending_count || 0}</span> attending
                    </span>
                  </div>
                  {ev.description && <div className="mt-1.5 text-sm text-muted-foreground line-clamp-2">{ev.description}</div>}
                  <div className="mt-3 flex gap-2 flex-wrap">
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => setRsvpEvent(ev)} data-testid={`event-rsvps-${ev.id}`}>
                      <Users className="h-3.5 w-3.5 mr-1" /> RSVPs
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => startEdit(ev)} data-testid={`event-edit-${ev.id}`}><Edit className="h-3.5 w-3.5 mr-1" /> Edit</Button>
                    {ev.show_on_landing && (
                      <Button
                        size="sm"
                        variant={ev.pinned_on_landing ? "default" : "outline"}
                        className={`rounded-full ${ev.pinned_on_landing ? "bg-[hsl(var(--accent))] text-white hover:bg-[hsl(var(--accent))]/90 border-[hsl(var(--accent))]" : ""}`}
                        onClick={() => togglePin(ev)}
                        data-testid={`event-pin-${ev.id}`}
                        title={ev.pinned_on_landing ? "Unpin from landing" : "Pin to top of landing strip"}
                      >
                        {ev.pinned_on_landing ? (
                          <><PinOff className="h-3.5 w-3.5 mr-1" /> Unpin</>
                        ) : (
                          <><Pin className="h-3.5 w-3.5 mr-1" /> Pin</>
                        )}
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" onClick={() => setConfirmDel(ev)} data-testid={`event-del-${ev.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                  </div>
                </div>
              </div>
            </Card>
            );
          })}
        </div>
      )}

      {/* New / Edit event dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit event" : "New event"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-sm font-medium">Title</Label><Input value={form.title} onChange={(e) => set("title", e.target.value)} className="mt-1.5 h-11 rounded-lg" data-testid="event-form-title" /></div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label className="text-sm font-medium">Start</Label><Input type="datetime-local" value={form.start_at} onChange={(e) => set("start_at", e.target.value)} className="mt-1.5 h-11 rounded-lg" data-testid="event-form-start" /></div>
              <div><Label className="text-sm font-medium">End <span className="text-muted-foreground font-normal">(optional)</span></Label><Input type="datetime-local" value={form.end_at} onChange={(e) => set("end_at", e.target.value)} className="mt-1.5 h-11 rounded-lg" /></div>
            </div>
            <div><Label className="text-sm font-medium">Location</Label><Input value={form.location} onChange={(e) => set("location", e.target.value)} className="mt-1.5 h-11 rounded-lg" placeholder="Club ground, Main pavilion, etc." /></div>
            <div>
              <Label className="text-sm font-medium">
                Price <span className="text-muted-foreground font-normal">(AUD, 0 = free)</span>
              </Label>
              <div className="mt-1.5 relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm pointer-events-none">
                  $
                </span>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={
                    form.price_cents
                      ? (Number(form.price_cents) / 100).toFixed(2).replace(/\.00$/, "")
                      : ""
                  }
                  onChange={(e) => {
                    const raw = e.target.value.trim();
                    if (!raw) return set("price_cents", 0);
                    const dollars = Number(raw);
                    if (Number.isNaN(dollars) || dollars < 0) return;
                    set("price_cents", Math.round(dollars * 100));
                  }}
                  className="fit-input h-11 rounded-lg pl-7 pr-24"
                  placeholder="0.00"
                  data-testid="event-form-price"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-mono-sm text-muted-foreground pointer-events-none">
                  AUD
                </span>
              </div>
              <p className="mt-1.5 text-[11px] text-muted-foreground">
                Attending members pay this amount. Leave at $0 for free events (the Pay button is hidden).
              </p>
            </div>
            <div><Label className="text-sm font-medium">Description</Label><Textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-1.5 rounded-lg" /></div>

            {/* Image picker */}
            <div>
              <Label className="text-sm font-medium">Event image</Label>
              <div className="mt-1.5 border border-dashed border-border rounded-xl p-4 text-center bg-[hsl(var(--secondary))]/40">
                <input ref={fileRef} onChange={onFile} type="file" accept="image/png,.png" className="hidden" data-testid="event-image-input" />
                {form.image_url ? (
                  <div className="relative inline-block">
                    <img src={form.image_url} alt="preview" className="max-h-48 mx-auto rounded-lg object-cover" data-testid="event-image-preview" />
                    <button type="button" onClick={clearImage} className="absolute -top-2 -right-2 h-7 w-7 rounded-full bg-background border border-border shadow-md inline-flex items-center justify-center text-muted-foreground hover:text-destructive hover:border-destructive transition-colors" aria-label="Remove image" data-testid="event-image-clear">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <div className="py-5">
                    <ImageIco className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                    <div className="text-sm text-muted-foreground">Upload an image or pick one from the club gallery</div>
                    <div className="text-[11px] text-muted-foreground mt-1">
                      {form.image_orientation === "portrait"
                        ? "PNG, JPG, WebP · must be exactly 1080 × 1350 px (no compression)"
                        : "PNG, JPG, WebP · must be exactly 1920 × 1080 px (16:9, no compression)"}
                    </div>
                  </div>
                )}
                <div className="mt-3 flex flex-wrap gap-2 justify-center">
                  <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => fileRef.current?.click()} data-testid="event-image-upload-btn">
                    <Upload className="h-3.5 w-3.5 mr-1.5" /> {form.image_url ? "Replace upload" : "Upload from device"}
                  </Button>
                  <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => setGalleryOpen(true)} data-testid="event-image-gallery-btn">
                    <GalleryHorizontal className="h-3.5 w-3.5 mr-1.5" /> Choose from gallery
                  </Button>
                </div>
              </div>

              {/* Image orientation selector */}
              <div className="mt-3">
                <Label className="text-sm font-medium">Image orientation</Label>
                <div className="mt-1.5 flex gap-2">
                  <Button
                    type="button"
                    variant={form.image_orientation === "portrait" ? "default" : "outline"}
                    size="sm"
                    className="rounded-lg flex-1"
                    onClick={() => set("image_orientation", "portrait")}
                    data-testid="event-orientation-portrait"
                  >
                    <span className="inline-block w-3 h-4 border-2 border-current rounded-sm mr-2" />
                    Portrait
                  </Button>
                </div>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Controls the card shape on the landing page. Portrait = tall (4:5), Landscape = wide (16:9).
                </p>
              </div>
            </div>

            {/* Reminder configuration */}
            <div className="rounded-xl border border-border bg-[hsl(var(--secondary))]/30 p-4 space-y-4">
              <div className="flex items-center gap-2">
                <BellRing className="h-4 w-4 text-[hsl(var(--accent))]" />
                <Label className="text-sm font-semibold">Reminders</Label>
              </div>
              <p className="text-[11px] text-muted-foreground -mt-2">
                Reminders appear as in-app announcements on each member&rsquo;s dashboard the day they&rsquo;re scheduled. They never re-fire for the same day.
              </p>

              <div className="space-y-2">
                <Label className="text-xs font-medium inline-flex items-center gap-1.5">
                  <UserCheck className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-300" />
                  Remind members who RSVP&rsquo;d attending
                </Label>
                <Select value={form.attending_reminder} onValueChange={(v) => set("attending_reminder", v)}>
                  <SelectTrigger className="h-10 rounded-lg" data-testid="event-form-attending-reminder">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {REMINDER_OPTIONS.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}
                  </SelectContent>
                </Select>
                {form.attending_reminder !== "off" && (
                  <Textarea
                    rows={2}
                    value={form.attending_reminder_message}
                    onChange={(e) => set("attending_reminder_message", e.target.value)}
                    placeholder="Optional custom reminder message (leave empty for the default)"
                    className="rounded-lg text-sm"
                    data-testid="event-form-attending-reminder-message"
                  />
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-medium inline-flex items-center gap-1.5">
                  <UserX className="h-3.5 w-3.5 text-[hsl(var(--accent))]" />
                  Nudge members who haven&rsquo;t RSVP&rsquo;d yet
                </Label>
                <Select value={form.not_responded_reminder} onValueChange={(v) => set("not_responded_reminder", v)}>
                  <SelectTrigger className="h-10 rounded-lg" data-testid="event-form-not-responded-reminder">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {REMINDER_OPTIONS.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}
                  </SelectContent>
                </Select>
                {form.not_responded_reminder !== "off" && (
                  <Textarea
                    rows={2}
                    value={form.not_responded_reminder_message}
                    onChange={(e) => set("not_responded_reminder_message", e.target.value)}
                    placeholder="Optional custom nudge message (leave empty for the default)"
                    className="rounded-lg text-sm"
                    data-testid="event-form-not-responded-reminder-message"
                  />
                )}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Published</Label>
              <Switch checked={form.published} onCheckedChange={(v) => set("published", v)} />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium">Show on landing page</Label>
                <div className="text-[11px] text-muted-foreground mt-0.5">
                  Featured in the public homepage strip until the event starts.
                </div>
              </div>
              <Switch
                checked={!!form.show_on_landing}
                onCheckedChange={(v) => set("show_on_landing", v)}
                data-testid="event-form-show-on-landing"
              />
            </div>
            <Button onClick={save} className="w-full h-11 btn-primary-modern" data-testid="event-form-save">Save event</Button>
          </div>
        </DialogContent>
      </Dialog>

      <GalleryPickerDialog open={galleryOpen} onOpenChange={setGalleryOpen} currentUrl={form.image_url} onSelect={pickFromGallery} />
      <RsvpListDialog event={rsvpEvent} onOpenChange={(v) => { if (!v) setRsvpEvent(null); }} />

      {/* Pin confirmation dialog — every pin auto-expires at end of game day */}
      <Dialog open={!!pinDialogEvent} onOpenChange={(o) => { if (!o) setPinDialogEvent(null); }}>
        <DialogContent className="rounded-2xl sm:max-w-md" data-testid="event-pin-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pin className="h-4 w-4" /> Pin to landing
            </DialogTitle>
            <DialogDescription>
              Pin <span className="font-medium text-foreground">{pinDialogEvent?.title}</span> to the front of the public landing strip. It'll lead the strip until the end of game day, then auto-unpin.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-xl border border-[hsl(var(--accent))]/30 bg-[hsl(var(--accent))]/5 p-3 text-sm" data-testid="event-pin-gameday-note">
            <div className="font-semibold">Auto-unpins</div>
            <div className="text-muted-foreground text-xs mt-0.5">
              {pinDialogEvent?.start_at
                ? new Date(new Date(pinDialogEvent.start_at).setHours(23, 59)).toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })
                : "after the fixture concludes"}
            </div>
          </div>
          <DialogFooter className="gap-2 mt-2">
            <Button variant="outline" onClick={() => setPinDialogEvent(null)} disabled={pinSaving} data-testid="event-pin-cancel">Cancel</Button>
            <Button
              onClick={confirmPin}
              disabled={pinSaving}
              className="bg-[hsl(var(--accent))] text-white hover:bg-[hsl(var(--accent))]/90"
              data-testid="event-pin-confirm"
            >
              {pinSaving ? "Pinning…" : "Pin event"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!confirmDel} onOpenChange={(o) => { if (!o) setConfirmDel(null); }}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete event</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <span className="font-medium text-foreground">{confirmDel?.title || "this event"}</span>? This will also remove its chat room and RSVPs. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="event-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="event-confirm-delete"
              onClick={async () => {
                const c = confirmDel;
                setConfirmDel(null);
                if (c?.id) await remove(c.id);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}


function RsvpListDialog({ event, onOpenChange }) {
  const [data, setData] = useState(null);
  const open = !!event;

  useEffect(() => {
    if (!event) { setData(null); return; }
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/events/${event.id}/rsvps`);
        if (alive) setData(data);
      } catch {
        if (alive) setData({ attending: [], cant_attend: [], attending_count: 0, cant_attend_count: 0 });
      }
    })();
    return () => { alive = false; };
  }, [event]);

  const priceCents = Number(data?.price_cents || 0);
  const isPaid = priceCents > 0;
  const paidCount = Number(data?.paid_count || 0);
  const unpaidCount = Number(data?.unpaid_count || 0);
  const priceLabel = isPaid ? `$${(priceCents / 100).toFixed(2)}` : "Free";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="event-rsvps-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-[hsl(var(--accent))]" />
            <span className="truncate">RSVPs · {event?.title}</span>
          </DialogTitle>
        </DialogHeader>
        {!data ? (
          <div className="py-12 text-center text-muted-foreground text-sm">Loading…</div>
        ) : (
          <div className="space-y-4 mt-2">
            {/* Payment summary strip — shown only for paid events. Gives admins
                an at-a-glance view of chase-up work before drilling into the
                attendee list below. */}
            {isPaid && (
              <div
                className="rounded-xl border border-border bg-[hsl(var(--secondary))]/40 p-4 flex flex-wrap items-center gap-4"
                data-testid="event-rsvp-payment-summary"
              >
                <div className="flex items-center gap-2 text-sm">
                  <DollarSign className="h-4 w-4 text-[hsl(var(--accent))]" />
                  <span className="font-semibold">{priceLabel}</span>
                  <span className="text-muted-foreground">per attendee</span>
                </div>
                <div className="flex items-center gap-4 ml-auto">
                  <div className="text-xs text-muted-foreground">
                    <span className="text-emerald-600 dark:text-emerald-400 font-semibold text-base">
                      {paidCount}
                    </span>{" "}
                    paid
                  </div>
                  <div className="text-xs text-muted-foreground">
                    <span className="text-amber-600 dark:text-amber-400 font-semibold text-base">
                      {unpaidCount}
                    </span>{" "}
                    unpaid
                  </div>
                </div>
              </div>
            )}
            <RsvpGroup
              title="Attending"
              icon={<UserCheck className="h-4 w-4 text-emerald-600 dark:text-emerald-300" />}
              accent="emerald"
              users={data.attending}
              testid="rsvp-group-attending"
              emptyText="No one has RSVP&rsquo;d attending yet."
              showPayment={isPaid}
              priceCents={priceCents}
            />
            <RsvpGroup
              title="Can&rsquo;t attend"
              icon={<UserX className="h-4 w-4 text-muted-foreground" />}
              accent="muted"
              users={data.cant_attend}
              testid="rsvp-group-cant-attend"
              emptyText="No declines."
              showPayment={false}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function RsvpGroup({ title, icon, accent, users, testid, emptyText, showPayment, priceCents }) {
  const ring = accent === "emerald" ? "ring-emerald-500/20" : "ring-border";
  return (
    <div className={`rounded-xl border border-border bg-card p-4`} data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="inline-flex items-center gap-2 text-sm font-semibold">{icon}{title}</div>
        <Badge variant="outline" className="text-[11px]">{users.length}</Badge>
      </div>
      {users.length === 0 ? (
        <p className="text-xs text-muted-foreground py-2">{emptyText}</p>
      ) : (
        <div className="space-y-2">
          {users.map((u) => (
            <div key={u.rsvp_id} className={`flex items-center gap-3 p-2 rounded-lg ring-1 ${ring}`} data-testid={`rsvp-row-${u.user_id}`}>
              <Avatar className="h-8 w-8">
                {u.avatar_url ? <AvatarImage src={u.avatar_url} alt="" /> : null}
                <AvatarFallback className="text-[10px]">{initials(u.first_name, u.last_name)}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{u.first_name} {u.last_name}</div>
                <div className="text-[11px] text-muted-foreground truncate">
                  {u.member_id} {u.membership_type ? `· ${u.membership_type}` : ""}
                </div>
              </div>
              {showPayment && (
                u.paid ? (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] px-2 h-6 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30"
                    data-testid={`rsvp-paid-${u.user_id}`}
                    title={u.receipt_number ? `Receipt ${u.receipt_number}` : "Paid"}
                  >
                    <Check className="h-3 w-3" /> Paid ${(u.amount_cents_paid / 100).toFixed(2)}
                  </span>
                ) : (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] px-2 h-6 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/30"
                    data-testid={`rsvp-unpaid-${u.user_id}`}
                    title={priceCents ? `Owes $${(priceCents / 100).toFixed(2)}` : "Awaiting payment"}
                  >
                    Unpaid
                  </span>
                )
              )}
              <div className="text-[10px] text-muted-foreground tabular-nums">{fmt(u.responded_at)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function GalleryPickerDialog({ open, onOpenChange, currentUrl, onSelect }) {
  const [photos, setPhotos] = useState(null);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/gallery", { params: { status: "approved" } });
        if (alive) setPhotos(data || []);
      } catch {
        if (alive) setPhotos([]);
      }
    })();
    return () => { alive = false; };
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-w-3xl">
        <DialogHeader><DialogTitle>Pick from the club gallery</DialogTitle></DialogHeader>
        {photos === null ? (
          <div className="py-16 text-center text-muted-foreground text-sm">Loading gallery…</div>
        ) : photos.length === 0 ? (
          <div className="py-16 text-center text-muted-foreground text-sm">
            <ImageIco className="h-8 w-8 mx-auto mb-2 opacity-60" />
            No approved gallery photos yet. Approve photos in the Gallery panel first.
          </div>
        ) : (
          <ScrollArea className="max-h-[60vh] pr-2">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="event-gallery-picker-grid">
              {photos.map((g) => {
                const isSelected = currentUrl && currentUrl === g.image_url;
                return (
                  <button type="button" key={g.id} onClick={() => onSelect(g.image_url)}
                    className={`group relative aspect-square rounded-xl overflow-hidden border-2 transition-all ${isSelected ? "border-[hsl(var(--accent))] ring-2 ring-[hsl(var(--accent))]/40" : "border-transparent hover:border-[hsl(var(--accent))]/60"}`}
                    data-testid={`event-gallery-photo-${g.id}`}>
                    <img src={g.image_url} alt={g.title || "Gallery photo"} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    {isSelected && (<div className="absolute top-2 right-2 h-7 w-7 rounded-full bg-[hsl(var(--accent))] text-white flex items-center justify-center shadow-md"><Check className="h-4 w-4" /></div>)}
                    {g.title && (<div className="absolute inset-x-0 bottom-0 px-2 py-1.5 text-[11px] text-white truncate opacity-0 group-hover:opacity-100 transition-opacity">{g.title}</div>)}
                  </button>
                );
              })}
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
}
